<template>
  <div 
    v-if="isVisible"
    class="annotation-controls"
    :style="{ left: `${position.x}px`, top: `${position.y}px` }"
  >
    <!-- Drag Handle -->
    <div 
      class="controls-header"
      @mousedown="startDrag"
    >
      <span class="controls-title">
        <i class="fas fa-search"></i>
        {{ $t('annotation.zoom') }}
      </span>
      <button
        class="close-btn"
        :title="$t('common.close')"
        @click="$emit('close')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Zoom Slider -->
    <div class="controls-group zoom-slider-group">
      <input
        type="range"
        class="zoom-slider"
        min="25"
        max="400"
        :value="Math.round(zoom * 100)"
        @input="handleZoomSlider"
        :title="`${Math.round(zoom * 100)}%`"
      />
      <span class="zoom-display">{{ Math.round(zoom * 100) }}%</span>
    </div>

    <!-- Zoom Buttons -->
    <div class="controls-group">
      <button
        class="control-btn"
        :title="$t('annotation.zoomIn')"
        @click="$emit('zoom-in')"
      >
        <i class="fas fa-search-plus"></i>
      </button>
      <button
        class="control-btn"
        :title="$t('annotation.zoomOut')"
        @click="$emit('zoom-out')"
      >
        <i class="fas fa-search-minus"></i>
      </button>
    </div>

    <!-- Fit Controls -->
    <div class="controls-group">
      <button
        class="control-btn"
        :title="$t('annotation.fitToWidth')"
        @click="$emit('fit-to-width')"
      >
        <i class="fas fa-arrows-alt-h"></i>
      </button>
      <button
        class="control-btn"
        :title="$t('annotation.fitToPage')"
        @click="$emit('fit-to-page')"
      >
        <i class="fas fa-expand-arrows-alt"></i>
      </button>
      <button
        class="control-btn"
        :title="$t('annotation.actualSize')"
        @click="$emit('actual-size')"
      >
        <i class="fas fa-compress-arrows-alt"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  zoom: number;
  isVisible?: boolean;
}

withDefaults(defineProps<Props>(), {
  isVisible: true
});

const emit = defineEmits<{
  (e: 'zoom-in'): void;
  (e: 'zoom-out'): void;
  (e: 'zoom-reset'): void;
  (e: 'fit-to-screen'): void;
  (e: 'fit-to-width'): void;
  (e: 'fit-to-page'): void;
  (e: 'actual-size'): void;
  (e: 'zoom-to', value: number): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();

// Position state
const position = ref({ x: 0, y: 0 });
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

// Initialize position on mount
onMounted(() => {
  // Default position: bottom-right with some padding
  position.value = {
    x: window.innerWidth - 220,
    y: window.innerHeight - 380
  };
});

const handleZoomSlider = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const zoomPercent = parseInt(target.value);
  const zoomValue = zoomPercent / 100;
  emit('zoom-to', zoomValue);
};

// Drag functionality
const startDrag = (event: MouseEvent) => {
  isDragging.value = true;
  dragOffset.value = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y
  };
  
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  event.preventDefault();
};

const onDrag = (event: MouseEvent) => {
  if (!isDragging.value) return;
  
  position.value = {
    x: event.clientX - dragOffset.value.x,
    y: event.clientY - dragOffset.value.y
  };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});
</script>

<style scoped>
.annotation-controls {
  position: fixed;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 100;
  min-width: 200px;
}

.controls-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-sm);
  background: var(--surface-secondary);
  border-bottom: 1px solid var(--border-light);
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  cursor: move;
  user-select: none;
}

.controls-title {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.controls-title i {
  color: var(--primary);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--surface-hover);
  color: var(--danger);
}

.controls-group {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-xs) var(--space-sm);
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--surface-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--font-size-md);
}

.control-btn:hover {
  background: var(--surface-hover);
  border-color: var(--primary);
  color: var(--primary);
}

.zoom-display {
  min-width: 50px;
  text-align: center;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.zoom-slider-group {
  flex-direction: column;
  align-items: stretch;
  padding: var(--space-sm);
  border-bottom: 1px solid var(--border-light);
}

.zoom-slider {
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--border-light);
  outline: none;
  border-radius: 2px;
  margin-bottom: var(--space-xs);
}

.zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: var(--primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.zoom-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: var(--primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.zoom-slider:hover::-webkit-slider-thumb {
  background: var(--primary-dark);
}

.zoom-slider:hover::-moz-range-thumb {
  background: var(--primary-dark);
}

/* Responsive */
@media (max-width: 768px) {
  .annotation-controls {
    min-width: 180px;
  }

  .control-btn {
    width: 36px;
    height: 36px;
  }

  .controls-title {
    font-size: var(--font-size-xs);
  }
}
</style>
